import { cloneDeep } from 'lodash';
import { S1Endpoint } from 'common/graphqlTypes';
import { DataRow, EventAction, FieldsConfigItem } from 'plugins/types';
import { viewProcessGraphSrcProc, viewProcessGraphSrcProcParent, viewProcessGraphTgtProc } from './actions';

export const getActionUrl = (actionId: string, mgmtUrl: string, redirectPath: string) =>
  `${mgmtUrl}/${redirectPath}${actionId ? `?aid="${actionId}"` : ''}`;

/**
 * Extend log entry values with additional data from different source
 * @param values      log entry values
 * @param data        endpoint data
 */
export const getExtendedData = (values: DataRow, data?: S1Endpoint) => {
  // Enhance data with missing id field. Could serve for other needed fields in the future.
  const extendedData = values ? cloneDeep(values) : {};
  const { id } = data || {};
  if (id) {
    return {
      ...extendedData,
      id,
    };
  }
  return extendedData;
};

/**
 * Create filter for action request payload based on the action payloadConfig
 *
 * @param action
 * @param rowData
 */
const getActionPayloadFilter = (action: EventAction, rowData: DataRow) =>
  action.payloadConfig?.reduce(
    (acc, field: FieldsConfigItem) => ({
      ...acc,
      [field.fieldName]: rowData[field.fieldKey],
    }),
    {},
  );

/**
 * Create standard payload for action request according to the needed fields defined in action
 * configuration and data of current event
 *
 * @param action      data configuration to apply
 * @param rowData     data of selected event
 */
export const createActionPayload = (action: EventAction, rowData: DataRow) => {
  if (action.getPayload) {
    return action.getPayload(rowData);
  }
  const filter = getActionPayloadFilter(action, rowData);
  return { filter, data: {} };
};

const isBefore = (date1: Date, date2: Date) => date1 < date2;

/**
 * Function that returns whether the link to process graph should be displayed or not, depending on the field and data row values.
 * @param values    Values of the data row
 * @param field     Field id to determine the logic
 */
export const displayGraphRedirectProps = (values: DataRow, field: string): boolean => {
  if (!values['agent.uuid'] || !values['mgmt.url']) {
    return false;
  }
  const processStartTime = new Date(values['src.process.startTime'] as string);
  const eventTime = new Date(values['event.time'] as string);
  const isStartTimeValid = isBefore(processStartTime, new Date()) && isBefore(processStartTime, eventTime);

  if (!isStartTimeValid) {
    return false;
  }

  switch (field) {
    case 'src.process.uid':
      return !!(values['src.process.uid'] && values['src.process.storyline.id'] && values['src.process.startTime']);
    case 'src.process.parent.uid':
      return !!(
        values['src.process.startTime'] &&
        values['src.process.parent.storyline.id'] &&
        values['src.process.parent.uid']
      );
    case 'tgt.process.uid':
      return !!(values['tgt.process.storyline.id'] && values['tgt.process.uid'] && values['tgt.process.startTime']);
  }

  return false;
};

/**
 * Returns the action for redirecting to process graph, depending on which field triggered it.
 * @param field     the triggering field
 */
export const processGraphFieldToActionMapper = (field: string): EventAction | null => {
  switch (field) {
    case 'src.process.uid':
      return viewProcessGraphSrcProc;
    case 'src.process.parent.uid':
      return viewProcessGraphSrcProcParent;
    case 'tgt.process.uid':
      return viewProcessGraphTgtProc;
  }
  return null;
};
